/**
 * 批量事务抽屉 V5.0 - 重构后的Context Provider
 * 基于拆分Context的架构，组合SelectionContext和TransactionDataContext
 *
 * 重构说明：
 * 1. 拆分上帝Context为专职的Context
 * 2. 使用Hook封装业务逻辑
 * 3. 提升关注点分离和可维护性
 */

import React, { createContext, useContext } from "react";
import { SelectionProvider, useSelectionContext } from "./SelectionContext";
import { TransactionDataProvider, useTransactionDataContext } from "./TransactionDataContext";


// 创建Context
const BatchTransactionContext = createContext();

/**
 * 组合Provider组件 - 组合SelectionProvider和TransactionDataProvider
 */
export const BatchTransactionProvider = ({ children, initialFlights, transactionType }) => {
  return (
    <SelectionProvider initialFlights={initialFlights} transactionType={transactionType}>
      <BatchTransactionProviderInner initialFlights={initialFlights} transactionType={transactionType}>
        {children}
      </BatchTransactionProviderInner>
    </SelectionProvider>
  );
};

/**
 * 内部Provider - 在SelectionProvider内部，可以访问选择状态
 */
const BatchTransactionProviderInner = ({ children, initialFlights, transactionType }) => {
  const selectionManager = useSelectionContext();

  return (
    <TransactionDataProvider
      initialFlights={initialFlights}
      transactionType={transactionType}
      selectionManager={selectionManager}
    >
      <BatchTransactionContextProvider>
        {children}
      </BatchTransactionContextProvider>
    </TransactionDataProvider>
  );
};

/**
 * 最终的Context Provider - 组合所有状态和方法
 */
const BatchTransactionContextProvider = ({ children }) => {
  const selectionContext = useSelectionContext();
  const transactionDataContext = useTransactionDataContext();

  // 组合所有状态和方法，提供统一的接口
  const contextValue = {
    // 选择相关状态和方法
    ...selectionContext,

    // 事务数据相关状态和方法
    ...transactionDataContext,

    // 为了向后兼容，提供一些组合的计算属性
    state: {
      ...transactionDataContext.state,
      // 将选择状态合并到state中，保持向后兼容
      activeAirports: selectionContext.activeAirports,
      availableSuppliers: selectionContext.availableSuppliers,
      selectedSupplier: selectionContext.selectedSupplier,
      availableProducts: selectionContext.availableProducts,
      selectedContractItems: selectionContext.selectedContractItems,
      availableAirports: selectionContext.availableAirports,
    }
  };

  return (
    <BatchTransactionContext.Provider value={contextValue}>
      {children}
    </BatchTransactionContext.Provider>
  );
};

// Hook for using context
export const useBatchTransactionContext = () => {
  const context = useContext(BatchTransactionContext);
  if (!context) {
    throw new Error("useBatchTransactionContext must be used within a BatchTransactionProvider");
  }
  return context;
};

export default BatchTransactionContext;